import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoCircle, IcoRefreshCw, IcoMail, IcoBriefcase, IcoPlus, IcoCheck, IcoClose, IcoAlertTriangle } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';

function SetupManagers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [managers, setManagers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [assistantAdmins, setAssistantAdmins] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddManager, setShowAddManager] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [viewEmployeesModal, setViewEmployeesModal] = useState(null);
  const [assignDepartmentsModal, setAssignDepartmentsModal] = useState(null);
  const [manageDepartmentsModal, setManageDepartmentsModal] = useState(null);
  const [assignEmployeeModal, setAssignEmployeeModal] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(null);

  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isAdminAssistant = user?.role === 'adminassistant';
  const canManage = isSuperAdmin || isAdminAssistant;

  const [newManager, setNewManager] = useState({
    firstName: '', lastName: '', email: '', hourlyRate: 0, role: 'manager'
  });

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchManagers();
    fetchAdminUsers();
    fetchAssistantAdmins();
    fetchSuperAdmins();
    fetchEmployees();
    fetchDepartments();
  }, [location.key]);

  const getToken = () => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser).token : null;
  };

  const getOrgId = () => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser).organizationId : null;
  };

  const fetchByRole = async (role, setter) => {
    try {
      const res = await fetch(`/api/staff?role=${role}&organizationId=${getOrgId()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setter(await res.json());
    } catch (err) {
      console.error(`Error fetching ${role}:`, err);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await fetch(`/api/staff?role=manager&organizationId=${getOrgId()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) setManagers(await response.json());
    } catch (error) {
      console.error('Error fetching managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers     = () => fetchByRole('admin', setAdminUsers);
  const fetchAssistantAdmins = () => fetchByRole('adminassistant', setAssistantAdmins);
  const fetchSuperAdmins    = () => fetchByRole('superadmin', setSuperAdmins);

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`/api/staff?role=employee&organizationId=${getOrgId()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) setEmployees(await response.json());
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`/api/departments?organizationId=${getOrgId()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) setDepartments(await response.json());
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSendInvite = async (manager) => {
    if (!window.confirm(`Send an invitation email to ${manager.firstName} ${manager.lastName} at ${manager.email}?`)) return;
    setInviteLoading(manager._id);
    try {
      const response = await fetch('/api/invite/manager', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerId: manager._id })
      });
      const data = await response.json();
      if (response.ok) { alert(`✅ Invitation sent to ${manager.email}!`); fetchManagers(); }
      else alert(`❌ Error: ${data.message}`);
    } catch { alert('❌ Error sending invitation'); }
    finally { setInviteLoading(null); }
  };

  const handleResendInvite = async (manager) => {
    if (!window.confirm(`Resend invitation to ${manager.firstName} ${manager.lastName}? This will generate a new link.`)) return;
    setInviteLoading(manager._id);
    try {
      const response = await fetch('/api/invite/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerId: manager._id })
      });
      const data = await response.json();
      if (response.ok) { alert(`✅ New invitation sent to ${manager.email}!`); fetchManagers(); }
      else alert(`❌ Error: ${data.message}`);
    } catch { alert('❌ Error resending invitation'); }
    finally { setInviteLoading(null); }
  };

  const StatusBadge = ({ manager }) => {
    const isActive = manager.status === 'active' || manager.inviteAccepted;
    const isPending = manager.status === 'pending' && manager.inviteToken;
    if (isActive) return <span style={{ padding: '0.25rem 0.75rem', background: '#d1fae5', color: '#065f46', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#22c55e"/> Active</span>;
    if (isPending) return <span style={{ padding: '0.25rem 0.75rem', background: '#fef3c7', color: '#92400e', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#f59e0b"/> Invite Sent</span>;
    return <span style={{ padding: '0.25rem 0.75rem', background: '#f3f4f6', color: '#6b7280', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCircle size={10} color="#9ca3af"/> No Invite Sent</span>;
  };

  // Only superadmin can send invites
  const InviteButton = ({ manager }) => {
    if (!isSuperAdmin) return null;
    const isLoading = inviteLoading === manager._id;
    const isActive = manager.status === 'active' || manager.inviteAccepted;
    const hasPendingInvite = manager.status === 'pending' && manager.inviteToken;
    if (isActive) return null;
    if (hasPendingInvite) return (
      <button onClick={() => handleResendInvite(manager)} disabled={isLoading}
        style={{ padding: '0.5rem 1rem', background: isLoading ? '#d1d5db' : '#f59e0b', color: isLoading ? '#9ca3af' : 'white', border: 'none', borderRadius: '6px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
        {isLoading ? 'Sending...' : <><IcoRefreshCw size={14}/> Resend Invite</>}
      </button>
    );
    return (
      <button onClick={() => handleSendInvite(manager)} disabled={isLoading}
        style={{ padding: '0.5rem 1rem', background: isLoading ? '#d1d5db' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: isLoading ? '#9ca3af' : 'white', border: 'none', borderRadius: '6px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
        {isLoading ? 'Sending...' : <><IcoMail size={14}/> Send Invite</>}
      </button>
    );
  };

  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          ...newManager,
          role: newManager.role || 'manager',
          organizationId: getOrgId(),
          password: Math.random().toString(36).slice(-12)
        })
      });
      if (response.ok) {
        const roleLabel = newManager.role === 'superadmin' ? 'Super Admin'
          : newManager.role === 'admin' ? 'Admin'
          : newManager.role === 'adminassistant' ? 'Assistant Admin'
          : 'Manager';
        alert(`✅ ${roleLabel} created! You can now send them an invitation email.`);
        setShowAddManager(false);
        setNewManager({ firstName: '', lastName: '', email: '', hourlyRate: 0, role: 'manager' });
        fetchManagers();
      } else {
        const data = await response.json();
        alert(`❌ Error: ${data.message}`);
      }
    } catch { alert('❌ Error creating manager'); }
  };

  const handleAssignEmployee = async (employeeId, managerId, selectedDepartmentIds = []) => {
    try {
      const employee = employees.find(e => e._id === employeeId);
      const existingManagerIds = employee?.managerIds?.map(id => id.toString()) || [];
      const updatedManagerIds = [...new Set([...existingManagerIds, managerId.toString()])];
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerIds: updatedManagerIds })
      });
      if (!response.ok) { alert('❌ Error assigning employee to manager'); return; }
      if (selectedDepartmentIds.length > 0) {
        const updatedDepartmentIds = [...new Set([...(employee?.departmentIds || []), ...selectedDepartmentIds])];
        const deptResponse = await fetch(`/api/staff/${employeeId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ departmentIds: updatedDepartmentIds })
        });
        alert(deptResponse.ok ? '✅ Employee assigned to manager and departments!' : '✅ Employee assigned to manager (but department assignment failed)');
      } else {
        alert('✅ Employee assigned to manager!');
      }
      fetchEmployees();
    } catch { alert('❌ Error assigning employee'); }
  };

  const handleUnassignEmployee = async (employeeId, managerId, employeeName) => {
    if (!window.confirm(`Remove ${employeeName} from this manager?`)) return;
    try {
      const employee = employees.find(e => e._id === employeeId);
      if (!employee) return;
      const updatedManagerIds = (employee.managerIds || []).map(id => id.toString()).filter(id => id !== managerId.toString());
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ managerIds: updatedManagerIds })
      });
      if (response.ok) { alert('✅ Employee unassigned!'); fetchEmployees(); }
      else alert('❌ Error unassigning employee');
    } catch { alert('❌ Error unassigning employee'); }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) { alert('✅ Employee deleted!'); fetchEmployees(); }
      else alert('❌ Error deleting employee');
    } catch { alert('❌ Error deleting employee'); }
  };

  const handleAssignManagerToDepartment = async (managerId, departmentId) => {
    try {
      const response = await fetch(`/api/staff/${managerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ departmentId })
      });
      if (response.ok) { alert('✅ Manager assigned to department!'); fetchManagers(); }
      else alert('❌ Error assigning manager');
    } catch { alert('❌ Error assigning manager'); }
  };

  const handleUnassignManagerFromDepartment = async (managerId, departmentId, managerName) => {
    if (!window.confirm(`Remove ${managerName} from this department?`)) return;
    try {
      const manager = managers.find(m => m._id === managerId);
      if (!manager) return;
      const updatedDepartmentIds = manager.departmentIds?.filter(id => id !== departmentId) || [];
      const response = await fetch(`/api/staff/${managerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ departmentIds: updatedDepartmentIds })
      });
      if (response.ok) { alert('✅ Manager unassigned from department!'); fetchManagers(); }
      else alert('❌ Error unassigning manager');
    } catch { alert('❌ Error unassigning manager'); }
  };

  const getManagerEmployees = (managerId) =>
    employees.filter(emp => emp.managerIds?.map(id => id.toString()).includes(managerId.toString()));

  if (!user) return null;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1 style={{display:'flex',alignItems:'center',gap:'0.5rem'}}><IcoBriefcase size={24} color="#22c55e"/> Setup Managers</h1>
          <p className="subtitle">
            {isSuperAdmin
              ? 'Create manager accounts and send invitation emails to set their passwords'
              : 'Create manager accounts for your organization'}
          </p>
        </section>

        <div style={{ marginBottom: '2rem' }}>
          <button onClick={() => setShowAddManager(!showAddManager)}
            style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
            <><IcoPlus size={14}/> Add New Manager</>
          </button>
        </div>

        {showAddManager && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '700' }}>Create New Account</h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              After creating, send them an invitation email so they can set their own password.
            </p>
            <form onSubmit={handleCreateManager} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>First Name*</span>
                  <input type="text" value={newManager.firstName} onChange={e => setNewManager(p => ({ ...p, firstName: e.target.value }))} required style={{ padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Last Name*</span>
                  <input type="text" value={newManager.lastName} onChange={e => setNewManager(p => ({ ...p, lastName: e.target.value }))} required style={{ padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Email*</span>
                  <input type="email" value={newManager.email} onChange={e => setNewManager(p => ({ ...p, email: e.target.value }))} required style={{ padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Hourly Rate</span>
                  <input type="number" step="0.01" value={newManager.hourlyRate} onChange={e => setNewManager(p => ({ ...p, hourlyRate: parseFloat(e.target.value) }))} style={{ padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Role*</span>
                  <select value={newManager.role} onChange={e => setNewManager(p => ({ ...p, role: e.target.value }))}
                    style={{ padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '1rem' }}>
                    <option value="manager">Manager</option>
                    <option value="adminassistant">Assistant Admin</option>
                    {isSuperAdmin && <option value="admin">Admin</option>}
                    {isSuperAdmin && <option value="superadmin">Super Admin</option>}
                  </select>
                </label>
              </div>
              <div style={{ padding: '0.75rem 1rem', background: '#e0e7ff', borderRadius: '6px', fontSize: '0.875rem', color: '#3730a3' }}>
                No password needed — they will set their own password via invitation email.
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="submit" style={{ padding: '0.75rem 1.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoCheck size={14}/> Create Account</button>
                <button type="button" onClick={() => setShowAddManager(false)} style={{ padding: '0.75rem 1.5rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Managers ({managers.length})</h2>
          {loading ? <p>Loading managers...</p> : managers.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No managers yet. Create one above!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {managers.map(manager => {
                const assignedEmployees = getManagerEmployees(manager._id);
                return (
                  <div key={manager._id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{manager.firstName} {manager.lastName}</h3>
                          <StatusBadge manager={manager} />
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                          {manager.email} • {assignedEmployees.length} {assignedEmployees.length === 1 ? 'employee' : 'employees'}
                          {manager.departmentIds?.length > 0 && <span> • {manager.departmentIds.length} {manager.departmentIds.length === 1 ? 'department' : 'departments'}</span>}
                        </p>
                      </div>

                      {/* Both superadmin and adminassistant see management buttons */}
                      {canManage && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {/* Send Invite — superadmin only */}
                          <InviteButton manager={manager} />
                          <button onClick={() => setManageDepartmentsModal(manager._id)}
                            style={{ padding: '0.5rem 1rem', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            Manage Departments
                          </button>
                          <button onClick={() => setViewEmployeesModal(manager._id)}
                            style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            View Employees
                          </button>
                          <button
                            onClick={() => setSelectedManager(selectedManager === manager._id ? null : manager._id)}
                            style={{ padding: '0.5rem 1rem', background: selectedManager === manager._id ? '#22c55e' : '#f3f4f6', color: selectedManager === manager._id ? 'white' : '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            {selectedManager === manager._id ? 'Hide Employees' : 'Manage Employees'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Manage Employees inline panel — both roles */}
                    {canManage && selectedManager === manager._id && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '6px' }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>All Employees:</h4>
                        {employees.length === 0 ? (
                          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No employees found</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {employees.map(emp => {
                              const displayName = emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.email || `Employee ${emp._id.slice(-4)}`;
                              const isAssigned = emp.managerIds?.map(id => id.toString()).includes(manager._id.toString());
                              return (
                                <div key={emp._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: isAssigned ? '#e0e7ff' : 'white', borderRadius: '4px', border: isAssigned ? '1px solid #c7d2fe' : 'none' }}>
                                  <span style={{ fontWeight: isAssigned ? '600' : 'normal', color: isAssigned ? '#4338ca' : 'inherit' }}>
                                    {displayName} {isAssigned && <IcoCheck size={12} color="#22c55e"/>}
                                  </span>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {isAssigned ? (
                                      <button onClick={() => handleUnassignEmployee(emp._id, manager._id, displayName)}
                                        style={{ padding: '0.25rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                        Unassign
                                      </button>
                                    ) : (
                                      <button onClick={() => { setAssignEmployeeModal({ employeeId: emp._id, managerId: manager._id, employeeName: displayName }); setSelectedDepartments([]); }}
                                        style={{ padding: '0.25rem 0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                        Assign
                                      </button>
                                    )}
                                    {/* Delete — superadmin only */}
                                    {isSuperAdmin && (
                                      <button onClick={() => handleDeleteEmployee(emp._id, displayName)}
                                        style={{ padding: '0.25rem 0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Assistant Admins section (superadmin + admin) ── */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Assistant Admins ({assistantAdmins.length})</h2>
          {assistantAdmins.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No assistant admins yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {assistantAdmins.map(u => (
                <div key={u._id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{u.firstName} {u.lastName}</h3>
                      <StatusBadge manager={u} />
                      <span style={{ padding: '0.2rem 0.6rem', background: '#e0f2fe', color: '#0369a1', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700' }}>Assistant Admin</span>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{u.email}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <InviteButton manager={u} />
                    {isSuperAdmin && (
                      <button onClick={() => navigate(`/employee/${u._id}`)}
                        style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Admins section (superadmin + admin) ── */}
        {isSuperAdmin && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Admins ({adminUsers.length})</h2>
            {adminUsers.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No admins yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {adminUsers.map(u => (
                  <div key={u._id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{u.firstName} {u.lastName}</h3>
                        <StatusBadge manager={u} />
                        <span style={{ padding: '0.2rem 0.6rem', background: '#fde8d8', color: '#9a3412', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700' }}>Admin</span>
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{u.email}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <InviteButton manager={u} />
                      <button onClick={() => navigate(`/employee/${u._id}`)}
                        style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
                        Edit Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Super Admins section (admin-role only — read-only) ── */}
        {user?.role === 'admin' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Super Admins ({superAdmins.length})</h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Super Admin accounts are read-only. Profile changes are managed by the Super Admin themselves.</p>
            {superAdmins.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No super admins found.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {superAdmins.map(u => (
                  <div key={u._id} style={{ border: '1px solid #fef3c7', background: '#fffbeb', borderRadius: '8px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{u.firstName} {u.lastName}</h3>
                        <StatusBadge manager={u} />
                        <span style={{ padding: '0.2rem 0.6rem', background: '#fef3c7', color: '#92400e', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700' }}>Super Admin</span>
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{u.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* View Employees Modal */}
      {viewEmployeesModal && (() => {
        const manager = managers.find(m => m._id === viewEmployeesModal);
        const assignedEmployees = getManagerEmployees(viewEmployeesModal);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem' }} onClick={() => setViewEmployeesModal(null)}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '800px', width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>{manager?.firstName} {manager?.lastName}'s Team</h2>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{assignedEmployees.length} {assignedEmployees.length === 1 ? 'employee' : 'employees'}</p>
                </div>
                <button onClick={() => setViewEmployeesModal(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
              </div>

              {/* All employees with assign/unassign — available to both roles */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem' }}>All Employees</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {employees.map(emp => {
                    const displayName = emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.email || `Employee ${emp._id.slice(-4)}`;
                    const isAssigned = emp.managerIds?.map(id => id.toString()).includes(manager._id.toString());
                    return (
                      <div key={emp._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', background: isAssigned ? '#e0e7ff' : '#f9fafb', borderRadius: '8px', border: isAssigned ? '1px solid #c7d2fe' : '1px solid #e5e7eb' }}>
                        <div>
                          <div style={{ fontWeight: isAssigned ? '700' : '500', color: isAssigned ? '#4338ca' : '#111827', fontSize: '0.9rem' }}>
                            {displayName} {isAssigned && <IcoCheck size={12} color="#22c55e"/>}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{emp.email}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setAssignDepartmentsModal({ employeeId: emp._id, managerId: manager._id, employeeName: displayName })}
                            style={{ padding: '0.4rem 0.75rem', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                            Departments
                          </button>
                          <button
                            onClick={() => { navigate(`/employee/${emp._id}`); setViewEmployeesModal(null); }}
                            style={{ padding: '0.4rem 0.75rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                            View Profile
                          </button>
                          {isAssigned ? (
                            <button
                              onClick={() => handleUnassignEmployee(emp._id, manager._id, displayName)}
                              style={{ padding: '0.4rem 0.75rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                              Unassign
                            </button>
                          ) : (
                            <button
                              onClick={() => { setAssignEmployeeModal({ employeeId: emp._id, managerId: manager._id, employeeName: displayName }); setSelectedDepartments([]); setViewEmployeesModal(null); }}
                              style={{ padding: '0.4rem 0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                              Assign
                            </button>
                          )}
                          {/* Delete — superadmin only */}
                          {isSuperAdmin && (
                            <button
                              onClick={() => handleDeleteEmployee(emp._id, displayName)}
                              style={{ padding: '0.4rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Manage Departments Modal */}
      {manageDepartmentsModal && (() => {
        const manager = managers.find(m => m._id === manageDepartmentsModal);
        const managerDepartments = manager?.departmentIds || [];
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem' }} onClick={() => setManageDepartmentsModal(null)}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>{manager?.firstName} {manager?.lastName}'s Departments</h2>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Assign departments that this manager can manage</p>
                </div>
                <button onClick={() => setManageDepartmentsModal(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
              </div>
              {departments.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>No departments created yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {departments.map(dept => {
                    const isAssigned = managerDepartments.includes(dept._id);
                    return (
                      <div key={dept._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: isAssigned ? '#e0e7ff' : '#f9fafb', borderRadius: '8px', border: isAssigned ? '1px solid #c7d2fe' : '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: '1rem', height: '1rem', background: dept.color, borderRadius: '4px' }} />
                          <div>
                            <div style={{ fontWeight: '700', color: isAssigned ? '#4338ca' : 'inherit' }}>{dept.name} {isAssigned && <IcoCheck size={12} color="#4338ca"/>}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{dept.description}</div>
                          </div>
                        </div>
                        {isAssigned ? (
                          <button onClick={() => handleUnassignManagerFromDepartment(manager._id, dept._id, `${manager.firstName} ${manager.lastName}`)}
                            style={{ padding: '0.5rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                            Unassign
                          </button>
                        ) : (
                          <button onClick={() => handleAssignManagerToDepartment(manager._id, dept._id)}
                            style={{ padding: '0.5rem 0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                            Assign
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Assign Employee Modal */}
      {assignEmployeeModal && (() => {
        const manager = managers.find(m => m._id === assignEmployeeModal.managerId);
        const managerDepartments = manager?.departmentIds || [];
        const availableDepartments = departments.filter(d => managerDepartments.includes(d._id));
        const handleConfirmAssignment = () => {
          handleAssignEmployee(assignEmployeeModal.employeeId, assignEmployeeModal.managerId, selectedDepartments);
          setAssignEmployeeModal(null); setSelectedDepartments([]);
        };
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '2rem' }} onClick={() => { setAssignEmployeeModal(null); setSelectedDepartments([]); }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>Assign {assignEmployeeModal.employeeName}</h2>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>to {manager?.firstName} {manager?.lastName}</p>
              </div>
              {availableDepartments.length > 0 ? (
                <>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Select Departments (Optional)</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {availableDepartments.map(dept => (
                      <label key={dept._id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: selectedDepartments.includes(dept._id) ? '#e0e7ff' : '#f9fafb', borderRadius: '6px', cursor: 'pointer', border: selectedDepartments.includes(dept._id) ? '2px solid #22c55e' : '1px solid #e5e7eb' }}>
                        <input type="checkbox" checked={selectedDepartments.includes(dept._id)} onChange={e => { if (e.target.checked) setSelectedDepartments([...selectedDepartments, dept._id]); else setSelectedDepartments(selectedDepartments.filter(id => id !== dept._id)); }} style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }} />
                        <div style={{ width: '1rem', height: '1rem', background: dept.color, borderRadius: '4px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600' }}>{dept.name}</div>
                          {dept.description && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{dept.description}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ padding: '1.5rem', background: '#fef3c7', borderRadius: '6px', marginBottom: '1.5rem' }}>
                  <p style={{ color: '#92400e', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><IcoAlertTriangle size={14} color="#92400e"/> This manager is not assigned to any departments yet.</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleConfirmAssignment} style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                  Assign {selectedDepartments.length > 0 && `(${selectedDepartments.length} dept${selectedDepartments.length > 1 ? 's' : ''})`}
                </button>
                <button onClick={() => { setAssignEmployeeModal(null); setSelectedDepartments([]); }} style={{ padding: '0.75rem 1.5rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Manage Employee Departments Modal */}
      {assignDepartmentsModal && (() => {
        const manager = managers.find(m => m._id === assignDepartmentsModal.managerId);
        const employee = employees.find(e => e._id === assignDepartmentsModal.employeeId);
        const managerDepartments = manager?.departmentIds || [];
        const employeeDepartments = employee?.departmentIds || [];
        const availableDepartments = isSuperAdmin ? departments : departments.filter(d => managerDepartments.includes(d._id));
        const handleToggleDepartment = async (departmentId) => {
          const isCurrentlyAssigned = employeeDepartments.includes(departmentId);
          const updatedDepartments = isCurrentlyAssigned ? employeeDepartments.filter(id => id !== departmentId) : [...employeeDepartments, departmentId];
          try {
            const response = await fetch(`/api/staff/${assignDepartmentsModal.employeeId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
              body: JSON.stringify({ departmentIds: updatedDepartments })
            });
            if (response.ok) { alert(`✅ Department ${isCurrentlyAssigned ? 'removed' : 'added'}!`); fetchEmployees(); }
            else alert('❌ Error updating departments');
          } catch { alert('❌ Error updating departments'); }
        };
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002, padding: '2rem' }} onClick={() => setAssignDepartmentsModal(null)}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>{assignDepartmentsModal.employeeName}'s Departments</h2>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{isSuperAdmin ? 'Assign to any department' : `Assign to ${manager?.firstName}'s departments`}</p>
                </div>
                <button onClick={() => setAssignDepartmentsModal(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
              </div>
              {availableDepartments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}><p>No departments available</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {availableDepartments.map(dept => {
                    const isAssigned = employeeDepartments.includes(dept._id);
                    return (
                      <div key={dept._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: isAssigned ? '#e0e7ff' : '#f9fafb', borderRadius: '8px', border: isAssigned ? '1px solid #c7d2fe' : '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: '1rem', height: '1rem', background: dept.color, borderRadius: '4px' }} />
                          <div>
                            <div style={{ fontWeight: '700', color: isAssigned ? '#4338ca' : 'inherit' }}>{dept.name} {isAssigned && <IcoCheck size={12} color="#4338ca"/>}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{dept.description}</div>
                          </div>
                        </div>
                        <button onClick={() => handleToggleDepartment(dept._id)}
                          style={{ padding: '0.5rem 0.75rem', background: isAssigned ? '#ef4444' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>
                          {isAssigned ? 'Remove' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default SetupManagers;